/**
 * Keeping Tab inside a surface that covers the page.
 *
 * WHY THIS FILE EXISTS. Four surfaces in the app are modal — `ui/Modal.tsx`,
 * `layout/Sidebar.tsx`'s nav drawer, `vault/VaultPackDrawer.tsx` and
 * `player/FullscreenPlayer.tsx` — and each had hand-rolled the same wrap with its own copy
 * of the focusable-element selector. Three copies, three different lists: Modal's six
 * selectors, the Vault drawer's six written as one string, and Sidebar's two. A surface
 * whose list is short does not trap the element it left out, and nobody adding a fifth
 * surface would know which of the three to copy. The rule is one thing, so it is written
 * once here and the four call sites ask for it.
 *
 * NOT A HOOK, and deliberately. The four callers subscribe differently and correctly so:
 * Modal takes React's `onKeyDown` on its overlay, while the other three add a
 * capture-phase listener on `window`/`document` because they have no single React subtree
 * the event is guaranteed to pass through. A hook would have to own the subscription and
 * would therefore have to pick one, so what is shared is the computation and not the
 * plumbing.
 *
 * NO DOM QUERY OUTSIDE `root`. Everything here is scoped to the element passed in — this
 * module reads `document.activeElement` and nothing else global, and it changes focus only
 * to elements inside `root`.
 */

/**
 * Everything the browser will put a Tab stop on, as a selector.
 *
 * The `:not([disabled])` clauses matter: a disabled control is skipped by the browser, so
 * treating one as the last stop would wrap early and strand the real last control.
 * `[tabindex="-1"]` is excluded for the mirror-image reason — it is focusable by script
 * (which is how a dialog box itself takes focus) but is not in the Tab order.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The Tab stops inside `root`, in document order.
 *
 * Filtered on `offsetParent`, which is null for anything `display: none` — a collapsed
 * panel's buttons are in the markup and are not reachable, and counting them would put the
 * wrap point on a control the user cannot see. `document.activeElement` is kept regardless,
 * because an element can legitimately be focused while `offsetParent` is null (a
 * `position: fixed` one, most obviously) and dropping the currently focused node would make
 * the comparisons below never match.
 */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/**
 * Handles one Tab press for a modal surface, wrapping at either end.
 *
 * Call it only for `key === 'Tab'`; it does not check, because the callers already branch on
 * the key to handle Escape.
 *
 * Three cases, and the third is the one a naive trap misses:
 *   - Tab on the last stop goes to the first, and Shift+Tab on the first goes to the last.
 *   - Focus that is OUTSIDE `root` is pulled back in. That is where focus starts if the
 *     surface's move-in found nothing to focus, and without this branch Tab would walk the
 *     page underneath — visually covered, and in a state the surface says is not
 *     interactive.
 *   - Anything else is left alone, so Tab moves normally between the controls inside.
 *
 * @param root The surface's own element. Nothing outside it is read or focused.
 * @param event The keydown. `preventDefault` is called only when focus is being moved here.
 * @returns Whether this call moved focus, for a caller that needs to know it was handled.
 */
export function wrapTabFocus(
  root: HTMLElement,
  event: Pick<KeyboardEvent, 'shiftKey' | 'preventDefault'>
): boolean {
  const focusable = focusableWithin(root);
  const active = document.activeElement;

  if (focusable.length === 0) {
    // Nothing to Tab between. Hold focus on the surface itself rather than letting it
    // leave — an empty dialog is still a dialog, and Modal's box carries tabIndex={-1}
    // for exactly this.
    if (active instanceof HTMLElement && !root.contains(active)) {
      event.preventDefault();
      root.focus();
      return true;
    }
    return false;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
    return true;
  }
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (active instanceof HTMLElement && !root.contains(active)) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}
